import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { equals } from 'ramda'
import React from 'react'

import { createConfirmation } from '@/app/contexts/confirmation.context'
import {
  CATIQuestion,
  CampaignTemplateType,
  LocalOnAnswerType,
  LocalOpenEnded,
  MultiSelectConfig,
  OnAnswerType,
} from '@/app/module/campaigns/types'
import { unwrapActions, wrapActions } from '@/app/module/campaigns/utils/actions/wrap'

import ResponseCatiMultiSelect from './response-cati-multiselect'
import ResponseCatiSpecific from './response-cati-specific'
import ResponseTextOpenEnded from './response-text-open-ended'
import { AnswerType, ResponseTextProps } from './response-text-props'
import { defaultOnAnswerCATI, defaultOnAnswerCATINoActions, isOpenEndedModified } from './types/multimessage/content'

type Props = ResponseTextProps & {
  item: CATIQuestion
  type: CampaignTemplateType

  changeHandler: (item: CATIQuestion) => void
}

export default function ResponseCati(props: Props) {
  const {
    item,
    id,
    personalizationList,
    senderIds,
    whatsappSenderIds,
    orgId,
    token,
    getImageProps,
    getVoiceProps,
    files = {},
    timezone,
    timezones,
    countryCode,
    countriesTimezones,
    languages,
    defaultLanguage,
    changeHandler,
  } = props

  const [state, setState] = React.useState(fromProps(item))
  React.useEffect(() => {
    setState(fromProps(item))
  }, [item])

  const handleChange = (newItem: Partial<State>) => {
    const newState = {
      ...state,
      ...newItem,
    }

    const returnToProps = backToProps(newState)
    const updatedItem = {
      ...item,
      ...returnToProps,
    } as CATIQuestion

    changeHandler(updatedItem)
    setState(newState)
  }

  const actionProps = React.useMemo(
    () => ({
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files,
      hasTranscribe: false,
      index: props.index,
      languages,
      personalizationList,
      senderIds,
      timezone,
      timezones,
      transcribeEnabled: false,
      whatsappSenderIds,
      getImageProps,
      getVoiceProps,
    }),
    [
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files,
      languages,
      props.index,
      senderIds,
      personalizationList,
      timezone,
      timezones,
      whatsappSenderIds,
      getImageProps,
      getVoiceProps,
    ],
  )
  const apiProps = React.useMemo(() => ({ orgId, token }), [orgId, token])

  return (
    <div
      className="text-question-block"
      style={{
        marginTop: '2rem',
      }}
    >
      <Divider
        style={{
          margin: '1rem -24px',
        }}
      />
      <Tabs
        value={state.answerType}
        indicatorColor="primary"
        onChange={async (_, val) => {
          if (isModified(state)) {
            const confirmed = await createConfirmation({
              title: 'Change response type',
              content:
                'Switching between response types will discard your current response config. Do you want to continue?',
            })

            if (!confirmed) {
              return
            }
          }

          handleChange({
            ...generateContent(val),
            answerType: val,
          })
        }}
      >
        <Tab
          value={AnswerType.Text}
          label="Specific Response"
          style={{ zIndex: 2 }}
          icon={<Icon>chat</Icon>}
          wrapped
          className="campaign-specific-tab"
        />

        <Tab
          value={AnswerType.OpenEnded}
          label="Open Response"
          style={{ zIndex: 2 }}
          icon={<Icon>textsms</Icon>}
          wrapped
          className="campaign-open-ended-tab"
        />

        <Tab
          value={AnswerType.MultiSelect}
          label="Multi Select"
          style={{ zIndex: 2 }}
          icon={<Icon>done_all</Icon>}
          wrapped
          className="campaign-multiselect-tab"
        />
      </Tabs>
      <Divider
        style={{
          margin: '-1px -24px 1rem',
        }}
      />
      {state.answerType === AnswerType.OpenEnded && state.openEnded && (
        <ResponseTextOpenEnded
          actionProps={actionProps}
          apiProps={apiProps}
          campaignType={props.type}
          id={id}
          openEnded={state.openEnded}
          partType={item.type}
          transcribe={false}
          onChange={handleChange}
        />
      )}
      {state.answerType === AnswerType.Text && (
        <ResponseCatiSpecific
          actionProps={actionProps}
          apiProps={apiProps}
          campaignType={props.type}
          id={id}
          onAnswer={state.onAnswer}
          partType={item.type}
          onChange={handleChange}
        />
      )}
      {state.answerType === AnswerType.MultiSelect && (
        <ResponseCatiMultiSelect id={id} onAnswer={state.onAnswer} onChange={handleChange} />
      )}
    </div>
  )
}

const getDefaultOnAnswer = (answerType: AnswerType): LocalOnAnswerType[] => {
  switch (answerType) {
    case AnswerType.MultiSelect:
      return defaultOnAnswerCATINoActions
    case AnswerType.Text:
      return defaultOnAnswerCATI
    default:
      return []
  }
}

const fromOnAnswer = (onAnswer: OnAnswerType[] | undefined, answerType: AnswerType): LocalOnAnswerType[] => {
  const defaultOnAnswer = getDefaultOnAnswer(answerType)

  if (answerType === AnswerType.MultiSelect) {
    return onAnswer && onAnswer.length > 0
      ? onAnswer.map((answer) => ({
          ...answer,
          actions: [],
        }))
      : defaultOnAnswer
  }

  return onAnswer && onAnswer.length > 0
    ? onAnswer.map((answer) => ({
        ...answer,
        actions: unwrapActions(answer.actions || []),
      }))
    : defaultOnAnswer
}

const fromOpenEnded = (openEnded?: CATIQuestion['openEnded']): LocalOpenEnded | undefined => {
  if (!openEnded) {
    return undefined
  }

  return {
    ...openEnded,
    actions: unwrapActions(openEnded.actions || []),
  }
}

type State = {
  answerType: AnswerType
  multiSelect: MultiSelectConfig | undefined
  onAnswer: LocalOnAnswerType[]
  openEnded: LocalOpenEnded | undefined
}

const getDefaultAnswerType = (item: CATIQuestion): AnswerType => {
  if (item.openEnded) {
    return AnswerType.OpenEnded
  }
  if (item.multiSelect) {
    return AnswerType.MultiSelect
  }
  return AnswerType.Text
}

const fromProps = (item: CATIQuestion): State => {
  const { onAnswer, openEnded } = item
  const answerType = getDefaultAnswerType(item)

  return {
    answerType,
    multiSelect: item.multiSelect,
    onAnswer: fromOnAnswer(onAnswer, answerType),
    openEnded: fromOpenEnded(openEnded),
  }
}

const toOnAnswer = (onAnswer: LocalOnAnswerType[], openEnded: LocalOpenEnded | undefined): OnAnswerType[] =>
  openEnded ? [] : onAnswer.map((item) => ({ ...item, actions: wrapActions(item.actions) }))

const toOpenEnded = (openEnded: LocalOpenEnded | undefined): CATIQuestion['openEnded'] => {
  if (!openEnded) {
    return undefined
  }

  return {
    actions: wrapActions(openEnded.actions || []),
  }
}

const backToProps = ({ multiSelect, onAnswer, openEnded }: State): Partial<CATIQuestion> => ({
  multiSelect,
  onAnswer: toOnAnswer(onAnswer, openEnded),
  openEnded: toOpenEnded(openEnded),
})

type CATIQuestionContent = {
  multiSelect: MultiSelectConfig | undefined
  onAnswer: LocalOnAnswerType[]
  openEnded: LocalOpenEnded | undefined
}

const generateContent = (answerType: AnswerType): CATIQuestionContent => {
  switch (answerType) {
    case AnswerType.OpenEnded:
      return {
        multiSelect: undefined,
        onAnswer: [],
        openEnded: {
          actions: [],
        },
      }
    case AnswerType.MultiSelect:
      return {
        multiSelect: {},
        onAnswer: defaultOnAnswerCATINoActions,
        openEnded: undefined,
      }

    default:
      return {
        multiSelect: undefined,
        onAnswer: defaultOnAnswerCATI,
        openEnded: undefined,
      }
  }
}

const isModified = ({ answerType, onAnswer, openEnded }: State) => {
  const defaultOnAnswer = answerType === AnswerType.MultiSelect ? defaultOnAnswerCATINoActions : defaultOnAnswerCATI

  const isAnswerModfied =
    (answerType === AnswerType.Text || answerType === AnswerType.MultiSelect) &&
    onAnswer.length > 0 &&
    !equals(onAnswer, defaultOnAnswer)
  const isOpenModified = answerType === AnswerType.OpenEnded && isOpenEndedModified(openEnded)

  return isAnswerModfied || isOpenModified
}

export type { LocalOnAnswerType, LocalOpenEnded as LocalAction, State }
export { backToProps, fromProps, generateContent, isModified }
